import { useEffect, useMemo, useRef, useState } from "react";

import {
  ALOCACAO_DESATUALIZADA_MESSAGE,
  ALOCACAO_ERROR_MESSAGE,
  EQUIPE_OBRA_FORBIDDEN_MESSAGE,
  useAtualizarPeriodoAlocacao,
} from "@/modules/recursos";
import type { DivergenciasAlocacao, EquipeObraTarget, useEquipeObra } from "@/modules/recursos";
import type { MembroEquipeSimulado } from "@/modules/recursos/simulacao";
import type { EquipeRow } from "@/shared/components/equipe-obra/equipe-obra.types";
import { EQUIPE_SUCCESS_DETAIL, EQUIPE_SUCCESS_SUMMARY } from "../definir-equipe-obra.constants";
import { isRowAlterada, rowFromAlocacao } from "../definir-equipe-obra.utils";
import type { EquipeObraNotice } from "../types-definir-equipe-obra";

interface UseEquipeGravadaParams {
  obra: EquipeObraTarget;
  /** Obra com equipe já gravada (HU 2051); sem isso o hook fica inerte. */
  enabled: boolean;
  /** Consulta da equipe gravada (`useEquipeObra`), feita pela tela porque o período da obra vem dela. */
  equipe: ReturnType<typeof useEquipeObra>;
  /** As alocações carregadas viram as linhas da tabela. */
  carregarLinhas: (linhas: EquipeRow[]) => void;
  /** O PATCH da linha deu certo: ela volta à leitura com o período e a versão gravados. */
  registrarGravada: (
    id: string,
    gravado: { inicio: string; fim: string; updatedAt?: string },
  ) => void;
  /** O servidor recusou as datas da linha (400 com `details`): ela volta à edição com o erro no campo. */
  reabrirComErro: (id: string, errors: { inicio?: string; fim?: string }) => void;
  /** O PATCH da linha falhou: a linha fica marcada até a próxima tentativa. */
  marcarFalha: (id: string) => void;
  /** O aviso dentro do modal (o Toast da tela fica atrás dele). */
  avisar: (mensagem: string) => void;
  onNotify: (notice: EquipeObraNotice) => void;
  onSaved: () => void;
  onHide: () => void;
  /**
   * O servidor apontou conflito/indisponibilidade (409 `DIVERGENCIAS_ALOCACAO`) ao gravar:
   * a tela reabre os modais com esses dados e reenvia com a justificativa.
   */
  onDivergencias: (divergencias: DivergenciasAlocacao) => void;
  /** Períodos já no cenário: a linha reabre com a data simulada, não a da carga crua. */
  overlaySimulacao?: readonly MembroEquipeSimulado[];
}

/**
 * Edição da equipe já gravada dentro do modal "Definir Equipe de Obra" (HU 2051): carrega as
 * alocações, monta as linhas e, no Salvar, envia um PATCH `{inicio, fim}` por linha alterada.
 */
export function useEquipeGravada({
  obra,
  enabled,
  equipe,
  carregarLinhas,
  registrarGravada,
  reabrirComErro,
  marcarFalha,
  avisar,
  onNotify,
  onSaved,
  onHide,
  onDivergencias,
  overlaySimulacao,
}: UseEquipeGravadaParams) {
  const { atualizar } = useAtualizarPeriodoAlocacao();
  const [saving, setSaving] = useState(false);
  const savedAny = useRef(false);

  // A referência do overlay costuma ser estável; a chave por conteúdo protege contra quem passa
  // um objeto novo a cada render, o que recarregaria as linhas e descartaria as edições.
  const overlayChave = JSON.stringify(overlaySimulacao ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- a chave de conteúdo é a identidade
  const overlayEstavel = useMemo(() => overlaySimulacao, [overlayChave]);

  useEffect(() => {
    if (enabled && equipe.status === "success") {
      // A alocação identifica a linha: o mesmo profissional pode ocupar duas. Sem `alocacaoId` no
      // cenário (membro de uma equipe nova), vale o profissional.
      const membros = overlayEstavel ?? [];
      const porAlocacao = new Map(
        membros.flatMap((membro) =>
          membro.alocacaoId ? [[membro.alocacaoId, membro] as const] : [],
        ),
      );
      const porUsuario = new Map(
        membros.filter((membro) => !membro.alocacaoId).map((membro) => [membro.usuarioId, membro]),
      );
      carregarLinhas(
        equipe.alocacoes.map((alocacao) => {
          const row = rowFromAlocacao(alocacao);
          const membro = porAlocacao.get(alocacao.id) ?? porUsuario.get(alocacao.usuarioId);
          return membro
            ? {
                ...row,
                inicio: membro.inicio,
                fim: membro.fim,
                papelObraId: membro.papelObraId || row.papelObraId,
              }
            : row;
        }),
      );
    }
  }, [enabled, equipe.status, equipe.alocacoes, overlayEstavel, carregarLinhas]);

  /**
   * Grava as linhas alteradas (cada PATCH com `If-Match` = `updatedAt` lido). `justificativa`
   * acompanha todos os PATCH; `summary` troca o título do aviso de sucesso.
   */
  async function salvar(
    rows: EquipeRow[],
    { justificativa, summary }: { justificativa?: string; summary?: string } = {},
  ) {
    const alteradas = rows.filter(isRowAlterada);
    if (saving || alteradas.length === 0) return;

    setSaving(true);
    const avisos = new Set<string>();
    let falhas = 0;
    let divergencias: DivergenciasAlocacao | null = null;
    let desatualizada = false;

    for (const row of alteradas) {
      const result = await atualizar(
        obra.id,
        row.id,
        { inicio: row.inicio, fim: row.fim },
        { justificativa, ifMatch: row.updatedAt },
      );

      if (result.ok) {
        savedAny.current = true;
        const gravado = {
          inicio: result.alocacao?.inicio || row.inicio,
          fim: result.alocacao?.fim || row.fim,
        };
        registrarGravada(row.id, { ...gravado, updatedAt: result.alocacao?.updatedAt });
        continue;
      }

      falhas += 1;

      if (result.divergencias) {
        // As linhas seguintes só são gravadas depois da confirmação com justificativa.
        divergencias = result.divergencias;
        break;
      }

      if (result.desatualizada) {
        desatualizada = true;
        avisos.add(result.message ?? ALOCACAO_DESATUALIZADA_MESSAGE);
        break;
      }

      const { inicio, fim } = result.fieldErrors;

      if (inicio || fim) {
        // 400 com `details.inicio/fim`: o erro aparece no campo e a linha volta à edição.
        reabrirComErro(row.id, { inicio, fim });
        continue;
      }

      marcarFalha(row.id);
      avisos.add(
        result.forbidden
          ? EQUIPE_OBRA_FORBIDDEN_MESSAGE
          : (result.message ?? ALOCACAO_ERROR_MESSAGE),
      );
    }

    setSaving(false);

    for (const aviso of avisos) onNotify({ severity: "error", summary: aviso });
    const [primeiroAviso] = avisos;
    if (primeiroAviso) avisar(primeiroAviso);

    // Outra pessoa alterou a equipe: recarrega para trazer as datas e versões atuais.
    if (desatualizada) equipe.reload();
    if (divergencias) onDivergencias(divergencias);

    if (falhas === 0) {
      onNotify({
        severity: "success",
        summary: summary ?? EQUIPE_SUCCESS_SUMMARY,
        detail: summary ? undefined : EQUIPE_SUCCESS_DETAIL,
      });
      onSaved();
    }
  }

  /** Fecha o modal; se alguma linha foi gravada nesta sessão, a tela precisa recarregar. */
  function fechar() {
    if (saving) return;
    if (savedAny.current) onSaved();
    else onHide();
  }

  return { equipe, saving, salvar, fechar };
}
